import type { SceneDialogStatisticDto, ScenarioGroupDto } from '@/api-gen/api.schemas';
import type { UseQueryOptions } from '@tanstack/react-query';
import { courseGetUserScheduledCourseResults } from '@/api-gen/api.ts';
import {
  scenariosGetTrendingScenarioIds,
  scenariosGetRecommendedScenarios,
  scenariosGetDialogStatisticScenarioVersion,
  sceneGetSceneGroups
} from '@/api-gen/api.ts';

import type { ProcessDialogCommand, ScenarioDto, SynthesizeSpeechCommand, DialogSessionInitSessionParams, AddDialogStatisticCommand } from "@/api-gen/api.schemas";
import { PersonaUsage } from '@/api-gen/api.schemas';
import { EndReason } from "@/api-gen/api.schemas";
import { useMutation, useQuery } from "@tanstack/react-query"
import { dialogSessionInitSession, processRespondToUser, dialogStatisticsAddDialogStatistic, speechSynthesizeSpeech } from '@/api-gen/api.ts';

const QUERY_KEY = 'scenarios';

export const useInitSession = (scenarioId?: number, versionId?: number) => {
  const query = useMutation({
    mutationFn: async () => {
      const params: DialogSessionInitSessionParams | undefined = versionId ? { versionId } : undefined;
      const response = await dialogSessionInitSession(scenarioId!, params)
      return response.data
    }
  })
  return { initSession: query.mutateAsync, ...query }
}

export const useGenerateResponse = (sessionId: string) => {
  const query = useMutation({
    mutationFn: async ({ text, speech }: { text: string, speech: string }) => {
      const payload: ProcessDialogCommand = {
        sessionId: sessionId,
        text: text,
        speech: speech,
      };
      const response = await processRespondToUser(payload)
      return response.data
    },
  })
  return { generateResponse: query.mutateAsync, ...query }
}

export const useEndSession = (sessionId: string) => {
  const query = useMutation({
    mutationFn: async () => {
      if (!sessionId) {
        console.error('sessionId is not defined');
        return;
      }
      const cmd: AddDialogStatisticCommand = { sessionId, endReason: EndReason.Finish };
      const response = await dialogStatisticsAddDialogStatistic(cmd)
      return response.data
    }
  })
  return { endSession: query.mutateAsync, ...query }
}

export const useTrendingScenarioIds = (companyId?: number) => {
  const query = useQuery({
    queryKey: [QUERY_KEY, 'trendingIds'],
    queryFn: async () => {
      const { data } = await scenariosGetTrendingScenarioIds({ companyId, count: 5 } as any);
      return data;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    enabled: !!companyId
  })
  return { ...query, trendingScenarioIds: query.data }
}

export const useRecommendedScenarioIds = (companyId?: number) => {
  const query = useQuery({
    queryKey: [QUERY_KEY, 'recommendedIds', companyId],
    queryFn: async () => {
      const { data } = await scenariosGetRecommendedScenarios();
      return data;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnReconnect: false,
    refetchOnWindowFocus: true,
    enabled: !!companyId
  });
  return { ...query, recommendedScenarioIds: query.data };
};

export const useSynthesizeIntro = (scenario?: ScenarioDto) => {
  // Determine if an Intro-usage persona is configured on the scenario
  const introScenarioPersona = scenario?.scenarioPersonas?.find(sp => sp.personaUsages?.includes(PersonaUsage.Intro));
  const introPersona = introScenarioPersona?.persona;
  // Determine Default persona
  const defaultScenarioPersona = scenario?.scenarioPersonas?.find(sp => sp.personaUsages?.includes(PersonaUsage.Default));
  const defaultPersona = defaultScenarioPersona?.persona;
  // Prefer Intro persona, otherwise Default persona
  const chosenPersona = introPersona ?? defaultPersona;

  // Build payload, preferring Intro persona, then Default persona; fall back to legacy scenario fields as last resort
  const payload: SynthesizeSpeechCommand | undefined = scenario?.playScenarioContextOn && !!chosenPersona
    ? {
        text: scenario?.scenarioContextIntro,
        service: chosenPersona?.ttsProvider,
        sex: chosenPersona?.gender,
        language: chosenPersona?.languageCode ?? scenario?.langCode,
        voiceName: chosenPersona?.voiceName ?? undefined,
        voiceId: chosenPersona?.voiceId ?? undefined,
        speed: scenario?.speed
      }
    : undefined;

  const enabled = !!payload && scenario?.playScenarioContextOn && !!scenario?.scenarioContextIntro;

  const query = useQuery({
    queryKey: [
      QUERY_KEY,
      'synthesizeSpeechQuery',
      payload?.text,
      chosenPersona?.id ?? 'no-intro-or-default-persona',
      payload?.voiceId ?? payload?.voiceName,
      payload?.sex,
      payload?.language,
      payload?.service
    ],
    queryFn: async () => {
      const response = await speechSynthesizeSpeech(payload!);
      return response.data;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: enabled,
    staleTime: 5 * 60 * 1000
  });

  return { ...query, introAudio: query.data };
}

export const getDialogStatisticScenarioVersionQuery = (
  scenarioId: number | undefined,
  versionId: number | undefined
): UseQueryOptions<SceneDialogStatisticDto> => ({
  queryKey: ['scenarioVersionQuery', scenarioId, versionId],
  queryFn: async () => {
    const { data } = await scenariosGetDialogStatisticScenarioVersion(scenarioId!, versionId!);
    return data as SceneDialogStatisticDto;
  },
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  keepPreviousData: true, //???
  enabled: !!scenarioId && !!versionId
});

export const getScenarioGroupsQuery: UseQueryOptions<ScenarioGroupDto[]> = {
  queryKey: ['scenarioGroups'],
  queryFn: async () => {
    const { data } = await sceneGetSceneGroups();
    return data as ScenarioGroupDto[];
  },
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  keepPreviousData: true
};

export const getScheduledCourseScenarios = (courseId: number | null, userId: number) => ({
  queryKey: ['scheduledCourseScenarios', userId, courseId],
  queryFn: async () => {
    const { data } = await courseGetUserScheduledCourseResults(courseId!, { userId });
    return data;
  },
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  keepPreviousData: false,
  enabled: !!courseId
});
